import { useId } from 'react';
import { Sparkles, Crown, Ticket, CandlestickChart } from 'lucide-react';
import type { GameLike } from './GameCard.js';
import { resolveLotteryArt, type LotteryArt } from './lotteryArt.js';
import type { Remaining } from './useCountdown.js';
import { formatDurationShort } from '../../lib/datetime/duration.js';
import type { LotteryState } from './cardMeta.js';

const FLAG_BASE = 'https://hatscripts.github.io/circle-flags/flags';
function flagUrl(cc: string): string {
  return `${FLAG_BASE}/${cc.toLowerCase()}.svg`;
}

interface LotteryMediaProps {
  game: GameLike;
  remaining: Remaining;
  state: LotteryState;
  t: (k: string) => string;
}

/** The media (top) area of a lottery card: flag art + centered medallion +
 *  a bottom status overlay (state pill + countdown). The card title,
 *  open/close meta, and play button live in GameCard's footer. */
export function LotteryMedia({ game, remaining, state, t }: LotteryMediaProps) {
  const art = resolveLotteryArt(game);
  const hasStatusInfo = !!game.lotteryStatus || !!game.ticketBefore;
  const imageBacked = art.kind === 'flag' || art.kind === 'stock';
  return (
    <>
      <BackgroundLayer art={art} />
      {imageBacked && (
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/40 to-black/80" />
      )}
      <div className="absolute inset-0 flex items-center justify-center">
        <ArtMedallion art={art} />
      </div>
      {hasStatusInfo && (
        <div className="absolute left-2 bottom-2 right-2 flex items-center justify-between gap-1.5 pointer-events-none">
          <StatusPill state={state} t={t} />
          {state === 'open' && !remaining.expired && !!game.ticketBefore && (
            <CountdownBadge
              remaining={remaining}
              label={formatDurationShort(remaining.hh * 3600 + remaining.mm * 60 + remaining.ss, t)}
            />
          )}
        </div>
      )}
    </>
  );
}

function StatusPill({ state, t }: { state: LotteryState; t: (k: string) => string }) {
  const label =
    state === 'open' ? t('lottery.open') :
    state === 'syncing' ? t('lottery.refreshing') :
    t('lottery.closed');
  const bg =
    state === 'open' ? 'bg-[var(--success)]/90' :
    state === 'syncing' ? 'bg-[var(--warning)]/90' :
    'bg-black/55';
  const text = state === 'closed' ? 'text-white/75' : 'text-white';
  const dot =
    state === 'open' ? 'bg-white animate-pulse' :
    state === 'syncing' ? 'bg-white animate-pulse' :
    'bg-white/50';
  return (
    <span className={`inline-flex items-center gap-1 ${bg} ${text} text-[10px] font-semibold uppercase rounded-full px-1.5 py-0.5 backdrop-blur-sm`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

function CountdownBadge({ remaining, label }: { remaining: Remaining; label: string }) {
  const urgent = remaining.hh === 0 && remaining.mm < 5;
  return (
    <span className={`text-[11px] font-semibold rounded-full px-1.5 py-0.5 backdrop-blur-sm tabular-nums ${urgent ? 'bg-[var(--danger)]/85 text-white' : 'bg-black/55 text-white'}`}>
      {label}
    </span>
  );
}

function BackgroundLayer({ art }: { art: LotteryArt }) {
  if (art.kind === 'flag') {
    return (
      <img src={flagUrl(art.cc)} alt="" aria-hidden loading="lazy"
        className="absolute inset-0 w-full h-full object-cover blur-md scale-[1.4]"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
    );
  }
  if (art.kind === 'stock' && art.cc) {
    return (
      <img src={flagUrl(art.cc)} alt="" aria-hidden loading="lazy"
        className="absolute inset-0 w-full h-full object-cover blur-md scale-[1.4] opacity-70"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
    );
  }
  if (art.kind === 'yi-ki') {
    return <YiKiBackground round={art.round} />;
  }
  return <div className="absolute inset-0 bg-[var(--bg-elevated)]" />;
}

function ArtMedallion({ art }: { art: LotteryArt }) {
  if (art.kind === 'flag') {
    return (
      <img src={flagUrl(art.cc)} alt="" loading="lazy"
        className="w-12 h-12 rounded-full shadow-[0_4px_12px_rgba(0,0,0,.5)] ring-2 ring-white/15 object-cover" />
    );
  }
  if (art.kind === 'yi-ki') {
    return <YiKiLantern round={art.round} />;
  }
  if (art.kind === 'stock') {
    return (
      <div className="relative">
        <CandlestickChart className="w-12 h-12 text-white/90" strokeWidth={2} />
        {art.cc && (
          <img src={flagUrl(art.cc)} alt="" loading="lazy"
            className="absolute -right-1 -bottom-1 w-5 h-5 rounded-full ring-2 ring-[var(--bg-elevated)] object-cover" />
        )}
      </div>
    );
  }
  if (art.kind === 'special') {
    const IconComp = art.iconName === 'Crown' ? Crown : Sparkles;
    return <IconComp className="w-12 h-12 text-[var(--accent)]" strokeWidth={2} />;
  }
  return <Ticket className="w-12 h-12 text-white/70" strokeWidth={2} />;
}

// Rotates per round so a grid of 147 rounds reads as distinct cards, not one tile repeated.
// All four stay in the deep-red family: Yi Ki is sold as a Chinese-style draw.
const YIKI_PALETTES = [
  ['#7a0d12', '#3d0508', '#1a0203'],
  ['#6b0a1e', '#33040d', '#160106'],
  ['#7d1a08', '#3b0a03', '#180401'],
  ['#5c0a0a', '#2c0404', '#130101'],
] as const;

const GOLD = '#f6c76b';

function FretCorner({ x, y, rotate }: { x: number; y: number; rotate: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate})`} fill="none" stroke={GOLD} strokeOpacity="0.55" strokeWidth="1.4">
      <path d="M0 22 V0 H22" />
      <path d="M5 22 V5 H22" />
      <path d="M10 16 V10 H16 V14 H13" />
    </g>
  );
}

function YiKiBackground({ round }: { round: number }) {
  const glowId = useId();
  const [from, via, to] = YIKI_PALETTES[round % YIKI_PALETTES.length]!;
  return (
    <>
      <div
        className="absolute inset-0"
        style={{ background: `radial-gradient(110% 95% at 50% 10%, ${from} 0%, ${via} 55%, ${to} 100%)` }}
      />
      <span
        aria-hidden
        className="absolute -right-1.5 -top-5 text-[120px] font-black leading-none opacity-[.13] select-none"
        style={{ color: GOLD, fontFamily: "'Noto Serif SC','Songti SC','SimSun',serif" }}
      >
        彩
      </span>
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 185 116" preserveAspectRatio="xMidYMid slice" aria-hidden>
        <defs>
          <radialGradient id={glowId}>
            <stop offset="0" stopColor="#ffb347" stopOpacity="0.45" />
            <stop offset="1" stopColor="#ffb347" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="92.5" cy="52" r="46" fill={`url(#${glowId})`} />
        <FretCorner x={7} y={7} rotate={0} />
        <FretCorner x={178} y={7} rotate={90} />
      </svg>
    </>
  );
}

function YiKiLantern({ round }: { round: number }) {
  const id = useId();
  const bodyId = `${id}-body`;
  const goldId = `${id}-gold`;
  const wide = round > 99;
  return (
    <svg width="64" height="78" viewBox="0 0 64 78" aria-hidden className="drop-shadow-[0_6px_12px_rgba(0,0,0,.5)]">
      <defs>
        <radialGradient id={bodyId} cx="40%" cy="35%" r="70%">
          <stop offset="0" stopColor="#ff7a5c" />
          <stop offset=".5" stopColor="#e3261f" />
          <stop offset="1" stopColor="#8a0c0c" />
        </radialGradient>
        <linearGradient id={goldId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff1b8" />
          <stop offset=".5" stopColor="#f2b631" />
          <stop offset="1" stopColor="#a86a00" />
        </linearGradient>
      </defs>
      <line x1="32" y1="0" x2="32" y2="8" stroke={`url(#${goldId})`} strokeWidth="2" />
      <rect x="20" y="7" width="24" height="6" rx="2" fill={`url(#${goldId})`} />
      <ellipse cx="32" cy="38" rx="26" ry="25" fill={`url(#${bodyId})`} />
      <path d="M32 13 Q16 38 32 63 M32 13 Q48 38 32 63" fill="none" stroke="#5e0606" strokeOpacity="0.45" strokeWidth="1.2" />
      <path d="M32 13 Q4 38 32 63 M32 13 Q60 38 32 63" fill="none" stroke="#5e0606" strokeOpacity="0.3" strokeWidth="1" />
      <rect x="20" y="61" width="24" height="6" rx="2" fill={`url(#${goldId})`} />
      <path d="M27 67 v8 M32 67 v10 M37 67 v8" stroke={`url(#${goldId})`} strokeWidth="2" strokeLinecap="round" />
      <ellipse cx="22" cy="26" rx="6" ry="3" fill="#fff" fillOpacity="0.25" transform="rotate(-30 22 26)" />
      <circle cx="32" cy="38" r="14.5" fill="#fff6e0" stroke={`url(#${goldId})`} strokeWidth="2.2" />
      <text
        x="32"
        y={wide ? 42.5 : 44}
        textAnchor="middle"
        fontSize={wide ? 12.5 : 16.5}
        fontWeight="900"
        fill="#8a0c0c"
        style={{ fontFamily: 'var(--font-mono)' }}
      >
        {round}
      </text>
    </svg>
  );
}
